import { useCallback, type ReactNode } from "react";
import { useDropzone, type FileRejection } from "react-dropzone";
import { Plus, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IconTile } from "@/components/ui/IconTile";
import { cn } from "@/lib/utils";

interface FileDropZoneProps {
  onDrop: (files: File[]) => void;
  /** Called with the files the gate refused, so a screen can say why. */
  onRejected?: (rejections: FileRejection[]) => void;
  /** The input's accessible name, for a screen that holds more than one zone. */
  inputLabel?: string;
  accept?: Record<string, string[]>;
  maxSize?: number;
  disabled?: boolean;
  /** Overrides the default prompt, for a zone that takes one kind of thing. */
  title?: string;
  /** Replaces the derived extension list when a screen words it its own way. */
  hint?: ReactNode;
  /** False for a zone that holds one file, such as a logo. */
  multiple?: boolean;
  /**
   * `default` is the full panel a page's upload stands on; `compact` is one
   * line, for a field among others in a form.
   */
  size?: "default" | "compact";
  /** The input's id, so a field's own label can name it. */
  inputId?: string;
  /**
   * A button under the prompt that opens the browser, for a zone that stands
   * beside another: its words, as "Add more". Its name adds the zone's title.
   */
  action?: string;
}

/** What the zone says: the drop while a file is over it, otherwise the prompt and its limits. */
function DropPrompt({
  dragging,
  multiple,
  compact,
  tiled,
  title,
  hint,
  acceptedTypes,
  maxSize,
}: {
  dragging: boolean;
  multiple: boolean;
  compact: boolean;
  /** One of a pair of zones, whose title the design sets heavier. */
  tiled: boolean;
  title: string | undefined;
  hint: ReactNode;
  acceptedTypes: string | undefined;
  maxSize: number | undefined;
}) {
  if (dragging) {
    return (
      <p className="text-sm font-medium text-primary">
        {multiple ? "Drop files here..." : "Drop the file here..."}
      </p>
    );
  }
  return (
    <div className={compact ? "min-w-0" : undefined}>
      <p
        className={cn(
          tiled ? "text-[15px] font-bold" : "font-medium",
          !tiled && (compact ? "text-[12.5px]" : "text-sm"),
        )}
      >
        {title ?? "Drag & drop or click to browse"}
      </p>
      {hint !== undefined ? (
        <div className="mt-1 text-xs text-muted-foreground">{hint}</div>
      ) : (
        acceptedTypes && (
          <p className="mt-1 text-xs text-muted-foreground">
            Accepted: {acceptedTypes}
          </p>
        )
      )}
      {maxSize && (
        <p className="mt-1 text-xs text-muted-foreground">
          Max size: {(maxSize / 1024 / 1024).toFixed(0)} MB
        </p>
      )}
    </div>
  );
}

/** The zone's upload mark: on the brand's tile for a zone beside another, as the design draws a pair. */
function ZoneMark({ tiled, compact }: { tiled: boolean; compact: boolean }) {
  if (tiled) {
    return (
      <span className="mb-3">
        <IconTile size="lg">
          <Upload />
        </IconTile>
      </span>
    );
  }
  return (
    <Upload
      aria-hidden
      className={cn(
        "shrink-0 text-muted-foreground",
        compact ? "size-5" : "mb-3 h-10 w-10",
      )}
    />
  );
}

/** The button under the prompt that opens the browser, named with the zone it adds to. */
function ZoneAction({
  words,
  zone,
  disabled,
  onOpen,
}: {
  words: string;
  zone: string | undefined;
  disabled: boolean;
  onOpen: () => void;
}) {
  return (
    <Button
      type="button"
      size="dense"
      variant="secondary"
      className="mt-2.5"
      aria-label={`${words} ${zone ?? ""}`.trim()}
      disabled={disabled}
      onClick={(event) => {
        // The zone opens the browser on a press too; one press, one browser.
        event.stopPropagation();
        onOpen();
      }}
    >
      <Plus aria-hidden />
      {words}
    </Button>
  );
}

export function FileDropZone({
  onDrop,
  onRejected,
  inputLabel,
  accept,
  maxSize,
  disabled = false,
  title,
  hint,
  multiple = true,
  size = "default",
  inputId,
  action,
}: FileDropZoneProps) {
  const handleDrop = useCallback(
    (accepted: File[], rejections: FileRejection[]) => {
      if (rejections.length > 0) onRejected?.(rejections);
      if (accepted.length > 0) {
        onDrop(accepted);
      }
    },
    [onDrop, onRejected],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop: handleDrop,
    accept,
    maxSize,
    disabled,
    multiple,
  });
  const compact = size === "compact";

  const acceptedTypes = accept
    ? Object.values(accept).flat().join(", ")
    : undefined;

  return (
    <div
      {...getRootProps()}
      className={cn(
        "flex cursor-pointer rounded-lg border-2 border-dashed transition-all duration-200",
        compact
          ? "flex-row items-center gap-3 px-4 py-3 text-left"
          : "flex-col items-center justify-center p-8 text-center",
        isDragActive
          ? "border-primary bg-primary/5 shadow-lg"
          : "border-border hover:border-primary/40 hover:bg-primary/[0.02]",
        disabled && "cursor-not-allowed opacity-50",
      )}
    >
      <input {...getInputProps({ id: inputId })} aria-label={inputLabel} />
      <ZoneMark tiled={action !== undefined} compact={compact} />
      <DropPrompt
        dragging={isDragActive}
        multiple={multiple}
        compact={compact}
        tiled={action !== undefined}
        title={title}
        hint={hint}
        acceptedTypes={acceptedTypes}
        maxSize={maxSize}
      />
      {action !== undefined && (
        <ZoneAction
          words={action}
          zone={title}
          disabled={disabled}
          onOpen={open}
        />
      )}
    </div>
  );
}
